import axios from "axios"
import qs from "qs"
import { buildWebStorage, setupCache } from "axios-cache-interceptor"
import { get } from "lodash"

import errorHandler from "./errorHandler"

// Per-PR / test backend previews: visiting `/pr/<id>` (handled in main.tsx) saves
// the prefix to localStorage; here we prepend it to the API host so requests hit
// the matching preview backend. `/pr/0` clears it back to the default.
export const API_PREFIX_KEY = "apiPrefix"

export function resolveBaseURL(): string {
  const fallback = import.meta.env.VITE_API_BASE_URL as string
  if (typeof window === "undefined") return fallback
  const prefix = window.localStorage.getItem(API_PREFIX_KEY)
  if (!prefix) return fallback
  try {
    const url = new URL(fallback)
    url.hostname = `${prefix}-${url.hostname}`
    return url.toString()
  } catch {
    return fallback
  }
}

const axiosInstance = axios.create({
  baseURL: resolveBaseURL(),
})

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    let user: any
    try {
      if (token) {
        user = JSON.parse(sessionStorage.getItem("user") || "{}") || {}
      }
    } catch {
      //
    }
    const includeProfileId = (config as any).includeProfileId !== false

    config.url = `${config.url ?? ""}?${qs.stringify(
      {
        ...(includeProfileId ? { profile_id: get(user, "id") } : {}),
        ...config.params,
      },
      // Laravel-style array params: `statuses[]=a&statuses[]=b`.
      { arrayFormat: "brackets" },
    )}`
    config.params = {}
    // Only attach the auth header when we actually have a token — public shop
    // requests from guests must not send "Bearer null".
    if (token) config.headers.set("Authorization", `Bearer ${token}`)
    config.headers.set("Accept", "application/json")
    const lang = localStorage.getItem("shop_lang")
    config.headers.set("Accept-Language", lang === "ru" ? "ru" : "uz")
    return config
  },
  (error) => error,
)

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    errorHandler(error)
    return error
  },
)

const headerInterpreter = (headers: any) => {
  if (headers["x-my-custom-header"]) {
    const seconds = Number(headers["x-my-custom-header"] || "0")
    if (seconds < 1) {
      return "dont cache"
    }

    return seconds
  }

  return "not enough headers"
}

export const API_CACHE_PREFIX = "axios-cache:"

// Cache entries are keyed by URL only, so a response fetched by one account would
// be replayed for the next one signing in on the same browser. Wiping them on every
// sign-in and sign-out keeps one session's data out of another's.
export function clearApiCache(): void {
  if (typeof window === "undefined") return
  Object.keys(localStorage)
    .filter((key) => key.startsWith(API_CACHE_PREFIX) || /^-?\d+$/.test(key))
    .forEach((key) => localStorage.removeItem(key))
}

const api = setupCache(axiosInstance, {
  storage: buildWebStorage(localStorage, API_CACHE_PREFIX),
  headerInterpreter,
  cachePredicate: ({ config }) => {
    const ttl = (config.cache as any)?.ttl ?? 0
    return ttl > 0
  },
})

export default api
